import styled from "styled-components";
import { ArrowRight, Clock, Sparkles, Target, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Hero() {
  const navigation = useNavigate();
  return (
    <HeroSection>
      <HeroGrid>
        <div>
          <Eyebrow>
            <Sparkles size={14} />
            Career Direction, Then Roadmap
          </Eyebrow>

          <Title>
            Confused about your <Highlight>developer career</Highlight>?
          </Title>

          <SubText>
            Find your best-fit direction, current level, and what to focus on
            next. No guessing. No tutorial-hopping. Just clarity that turns
            into an actual roadmap.
          </SubText>

          <CTAButton
            onClick={() => {
              navigation("/choose-career-page");
            }}
          >
            Take Free Career Clarity Test
            <ArrowRight size={20} />
          </CTAButton>

          <Meta>
            <Clock size={14} />
            Takes ~5 minutes · No login required · Instant result
          </Meta>
        </div>

        <ProofCard>
          <ProofLabel>What you unlock</ProofLabel>
          <ProofTitle>Clarity first. Execution next.</ProofTitle>

          <ProofList>
            <ProofItem>
              <Target size={16} />
              Direction matched to your skills and goals
            </ProofItem>
            <ProofItem>
              <CheckCircle2 size={16} />
              Level snapshot with strengths and gaps
            </ProofItem>
            <ProofItem>
              <Sparkles size={16} />
              Next-step roadmap instead of vague advice
            </ProofItem>
          </ProofList>
        </ProofCard>
      </HeroGrid>
    </HeroSection>
  );
}

const HeroSection = styled.section`
  padding: 160px 24px 120px;
  margin-top: -100px;
  background:
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.14), transparent 34%),
    radial-gradient(circle at top right, rgba(56, 189, 248, 0.14), transparent 38%);
`;

const HeroGrid = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(280px, 0.95fr);
  gap: 24px;
  align-items: center;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 16px 0 0;
  font-size: clamp(36px, 5vw, 54px);
  font-weight: 900;
  line-height: 1.15;
  letter-spacing: -0.02em;
  max-width: 820px;
  margin: 0 auto;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #f59e0b, #38bdf8);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SubText = styled.p`
  margin-top: 20px;
  font-size: 18px;
  line-height: 1.7;
  color: #9ca3af;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled.button`
  margin-top: 36px;
  padding: 18px 34px;
  border-radius: 14px;
  font-size: 18px;
  font-weight: 700;
  color: white;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  border: none;

  background: linear-gradient(90deg, #f59e0b, #d97706);
  box-shadow: 0 10px 40px rgba(245, 158, 11, 0.28);

  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 16px 55px rgba(245, 158, 11, 0.38);
  }
`;

const Meta = styled.div`
  margin-top: 14px;
  font-size: 14px;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;

  @media (max-width: 920px) {
    justify-content: center;
  }
`;

const ProofCard = styled.div`
  padding: 24px;
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.1), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.4);
  text-align: left;
`;

const ProofLabel = styled.div`
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #fbbf24;
`;

const ProofTitle = styled.div`
  margin-top: 10px;
  color: #f8fafc;
  font-size: 24px;
  line-height: 1.2;
  font-weight: 1000;
`;

const ProofList = styled.div`
  margin-top: 18px;
  display: grid;
  gap: 10px;
`;

const ProofItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 16px;
  background: rgba(2, 6, 23, 0.48);
  border: 1px solid rgba(148, 163, 184, 0.12);
  color: #cbd5e1;
  font-size: 13px;
  font-weight: 700;
`;
